import { ReactNode } from 'react';
import {
  createPersistentKeyValueLocalStorage,
  MOBILE_WALLET_ADDRESS_LS_KEY,
  WALLET_ADDRESS_LS_KEY,
} from '@orionprotocol/storage';
import { WalletConnectParameters } from '@wagmi/connectors';

import { GetWalletAPI } from './WalletAPI';
import {
  createCoinbaseAPI,
  createInjectedAPI,
  createWalletConnectAPI,
  guestAPI,
} from './walletAPIs';
import { InjectedWalletsNames } from './walletAPIs/injectedAPI';
import { iosAppAPI } from './walletAPIs/iosAppAPI';
import { telegramMiniAppAPI } from './walletAPIs';

export enum WalletsNames {
  Metamask = 'MetaMask', // all chains
  OKX = 'OKX', // all chains
  WalletConnect = 'WalletConnect', // all chains
  Coinbase = 'Coinbase wallet',
  SafePal = 'SafePal',
  TrustWallet = 'TrustWallet',
  TokenPocket = 'TokenPocket',
  Rabby = 'Rabby',
  Guest = 'guest',
  IosApp = 'iOSApp',
  TelegramMiniApp = 'TelegramMiniApp',
  Bitget = 'Bitget wallet',
}

export type Wallet = {
  name: string;
  getAPI?: GetWalletAPI;
  isHidden: boolean;
  supportsOnly?: 'desktop' | 'mobile';
  // supportedChains?: SupportedChainId[];
  disabled?: boolean;
  tooltipContent?: ReactNode;
  getErrorMessage?: (error: Error) => string | undefined;
};

export const keyValueStorage = createPersistentKeyValueLocalStorage();

type GetWalletsProps = {
  walletConnectProjectId: string;
  coinbaseAppName: string;
  metamaskTooltip?: ReactNode;
  wcQrModalOptions?: WalletConnectParameters['qrModalOptions'];
};

export const getWallets = ({
  walletConnectProjectId,
  coinbaseAppName,
  metamaskTooltip,
  wcQrModalOptions,
}: GetWalletsProps): Wallet[] => {
  let wallets: Wallet[] = [
    {
      name: WalletsNames.Metamask,
      getAPI: createInjectedAPI({
        name: InjectedWalletsNames.Metamask,
      }),
      tooltipContent: metamaskTooltip,
      isHidden: false,
    },
    {
      name: WalletsNames.WalletConnect,
      getAPI: createWalletConnectAPI({
        projectId: walletConnectProjectId,
        qrModalOptions: wcQrModalOptions,
      }),
      getErrorMessage: (error: Error): string | undefined => {
        if (error.message === 'User closed modal') return 'Cancelled';
        return undefined;
      },
      isHidden: false,
    },
  ];

  const ua = navigator.userAgent.toLowerCase();
  const isSafari =
    ua.includes('safari') &&
    !ua.includes('chrome') &&
    !ua.includes('crios') &&
    !ua.includes('android');

  if (!isSafari) {
    wallets = wallets.concat([
      {
        name: WalletsNames.OKX,
        getAPI: createInjectedAPI({ name: InjectedWalletsNames.OKX }),
        isHidden: false,
      },
      {
        name: WalletsNames.Bitget,
        getAPI: createInjectedAPI({ name: InjectedWalletsNames.BitgetWallet }),
        isHidden: false,
      },
      {
        name: WalletsNames.TokenPocket,
        getAPI: createInjectedAPI({ name: InjectedWalletsNames.TokenPocket }),
        isHidden: false,
      },
      {
        name: WalletsNames.SafePal,
        getAPI: createInjectedAPI(),
        isHidden: false,
      },
      {
        name: WalletsNames.Guest,
        getAPI: guestAPI({
          addressGetter: () => keyValueStorage.getItem(WALLET_ADDRESS_LS_KEY),
        }),
        isHidden: true,
      },
      {
        name: WalletsNames.IosApp,
        getAPI: iosAppAPI({
          addressGetter: () =>
            keyValueStorage.getItem(MOBILE_WALLET_ADDRESS_LS_KEY),
        }),
        isHidden: true,
      },
      {
        name: WalletsNames.TelegramMiniApp,
        getAPI: telegramMiniAppAPI({
          addressGetter: () =>
            new URLSearchParams(window.location.search).get('walletAddress') ??
            undefined,
        }),
        isHidden: true,
      },
      {
        name: WalletsNames.TrustWallet,
        getAPI: createInjectedAPI({ name: InjectedWalletsNames.TrustWallet }),
        isHidden: false,
      },
      {
        name: WalletsNames.Coinbase,
        getAPI: createCoinbaseAPI({
          appName: coinbaseAppName,
        }),
        isHidden: false,
      },
      {
        name: WalletsNames.Rabby,
        getAPI: createInjectedAPI({ name: InjectedWalletsNames.Rabby }),
        isHidden: false,
      },
    ]);
  }

  return wallets;
};

/* export function validateWalletNetwork(
  walletName: string | undefined,
  chainId: SupportedChainId,
): string {
  if (walletName !== undefined) {
    const wallet = wallets.find(({ name }) => name === walletName);
    if (wallet?.supportedChains && !wallet.supportedChains.includes(chainId)) {
      const networkName = config.chains[chainId]?.label ?? 'selected network';
      return `${walletName} does not support ${networkName}`;
    }
  }
  return '';
} */
